import React, { forwardRef, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import DatePicker from 'react-datepicker';
import { ChevronDown, Plus } from 'lucide-react';
import { Button, Dropdown, DropdownTrigger, DropdownMenu, DropdownItem } from '@heroui/react';
import useBreakpoint from '../../calendar-common/hooks/useBreakpoint.js';
import { startOfWeek, endOfWeek, parseIsoDate } from '../utils/weekUrlPathSync.js';

/**
 * CalendarWeekNav — presentational view switcher + date navigation for the
 * React Week View. Direct mirror of `calendar-month/components/CalendarMonthNav.js`
 * with three differences:
 *   1. prev/next advance by ONE WEEK (via `onPrevWeek` / `onNextWeek` props).
 *   2. the date button shows the WEEK RANGE (e.g. "Mar 3 – 9, 2026") instead of
 *      a single month label.
 *   3. the date picker is a plain single-day calendar — picking any day selects
 *      that day's week. (The legacy AngularJS week-number dropdown is NOT
 *      reproduced; a clean day picker whose selection the page snaps to a week
 *      is sufficient.)
 *
 * PRESENTATIONAL: this component does NOT dispatch `setDateRange` or push the
 * URL. It receives navigation callbacks as props from `CalendarWeekV2Page`,
 * which owns all setDateRange / URL / session logic (mirrors how
 * `CalendarMonthV2Page` drives `CalendarMonthNav`).
 */

// Admin flags arrive as legacy '1'/'0' strings OR Symfony booleans — accept
// both (String(true) === '1' silently broke disablePilotView/enableResourceView).
const truthyFlag = v => v === true || v === 1 || v === '1';

const VIEWS = [
	{ key: 'month', label: 'Month View', path: 'month' },
	{ key: 'week', label: 'Week View', path: 'week' },
	{ key: 'day', label: 'Day View', path: 'day' },
	{ key: 'resource', label: 'Stacked View', path: 'resource', settingsFlag: 'enableResourceView' },
	{ key: 'dispatch', label: 'Dispatch View', path: 'dispatch' },
	{ key: 'report', label: 'Pilot View', path: 'report', hiddenFlag: 'disablePilotView' }
];

const FOCUS_RING =
	'focus-visible:ring-2 focus-visible:ring-primary/55 focus-visible:outline-none focus-visible:ring-offset-2';

/**
 * Format a week range for the date button. Sunday-start convention (see
 * `weekUrlPathSync`). Collapses the shared month/year so the label reads
 * "Mar 3 – 9, 2026" when the week stays in one month, and
 * "Mar 30 – Apr 5, 2026" (or "Dec 29, 2025 – Jan 4, 2026") when it spans a
 * month/year boundary. Mirrors the legacy week label's collapsing behaviour.
 */
function formatWeekRange(anchor) {
	const start = startOfWeek(anchor);
	const end = endOfWeek(anchor);
	if (!start || !end) return '';
	const monthFmt = { month: 'short' };
	// 'en-US', not the browser locale: the datepicker popup under this button and
	// the grid day-headers (WeekGrid) are both English, so a localized month name
	// here put three languages in one control cluster ("серп. 9 – 15, 2026" over
	// "August 2026"). Localizing the product is a feature, not this label's job.
	const sMonth = start.toLocaleDateString('en-US', monthFmt);
	const eMonth = end.toLocaleDateString('en-US', monthFmt);
	const sDay = start.getDate();
	const eDay = end.getDate();
	const sYear = start.getFullYear();
	const eYear = end.getFullYear();
	if (sYear !== eYear) {
		return `${sMonth} ${sDay}, ${sYear} – ${eMonth} ${eDay}, ${eYear}`;
	}
	if (sMonth !== eMonth) {
		return `${sMonth} ${sDay} – ${eMonth} ${eDay}, ${eYear}`;
	}
	return `${sMonth} ${sDay} – ${eDay}, ${eYear}`;
}

const NavDateButton = forwardRef(function NavDateButton({ value, label, onClick, phone }, ref) {
	// react-datepicker clones this element and spreads its OWN `value` (the
	// single-day text formatted from `selected`) over our props, so it would win
	// over an explicitly-passed `value`. We therefore pass the week-range string
	// as a dedicated `label` prop (which react-datepicker never sets) and prefer
	// it over `value` here so the button shows "Mar 3 – 9, 2026", not one day.
	const display = label != null && label !== '' ? label : value;
	return (
		<button
			type="button"
			ref={ref}
			onClick={onClick}
			className={[
				'inline-flex items-center gap-1 px-3 py-1.5 text-cal-base font-semibold rounded-md',
				'border border-default-300 bg-default-50 hover:bg-default-100',
				'transition-colors duration-[var(--dur-hover)] ease-[var(--ease-out)]',
				// Phone: 44px touch target + `w-full` so the button fills its flex-1
				// wrapper (no middle gap); `justify-center` centers the label+chevron
				// in the widened control; the label span truncates so a long week
				// range never overflows the row.
				phone ? 'min-h-[44px] w-full justify-center' : '',
				FOCUS_RING
			].join(' ')}
		>
			<span className="select-none truncate">{display}</span>
			<ChevronDown className="w-4 h-4 opacity-70 shrink-0" aria-hidden="true" />
		</button>
	);
});

/**
 * NavActions — the Week View nav's action group: the view switcher
 * (dropdown ≤1023px / inline tabs ≥1024px) + the Create Event primary button.
 *
 * Extracted as a standalone component (identical to the Month View's) so the
 * page can compose it into a phone-only 2-row header WITHOUT threading layout
 * booleans through `CalendarWeekNav`. On non-phone `CalendarWeekNav` renders it
 * inline; on phone the page renders it in row 2 next to the Filters trigger.
 */
function NavActions({
	currentViewKey = 'week',
	orgUnique,
	onCreateEventClick,
	canCreateEvent = false
}) {
	// Org settings live under a view-specific bootstrap key (month/week/day
	// entrypoints register the shared bootstrapSlice under their own name) —
	// read whichever is populated so the view-switcher flags work on ALL
	// calendar pages. TODO(promote-lift): collapse to one shared key.
	const organizationSettings =
		useSelector(
			state =>
				state?.calendarMonthBootstrap?.organizationSettings ||
				state?.calendarWeekBootstrap?.organizationSettings ||
				state?.calendarDayBootstrap?.organizationSettings
		) || {};

	// Collapse to dropdown at ≤1023px so the tabs + Create Event row fits the
	// 768-1023px range without wrapping.
	const { navCompact: isNarrow, phone } = useBreakpoint();
	const touchTarget = phone ? 'min-h-[44px]' : '';

	const visibleViews = useMemo(() => {
		return VIEWS.filter(v => {
			if (v.settingsFlag && !truthyFlag(organizationSettings[v.settingsFlag])) return false;
			if (v.hiddenFlag && truthyFlag(organizationSettings[v.hiddenFlag])) return false;
			return true;
		});
	}, [organizationSettings]);

	const buildViewHref = viewPath => {
		if (!orgUnique) return '#';
		return `/org/${orgUnique}/calendar/${viewPath}/`;
	};

	return (
		<div className={`flex items-center gap-2 ${phone ? 'w-full flex-1 min-w-0' : 'flex-wrap'}`}>
			{/* Legacy Bootstrap CSS still ships `.hidden { display: none !important }`
			    which beats Tailwind's `md:flex`/`md:hidden`. Render exactly one of
			    the two branches via JS so neither sibling exists in the DOM at the
			    wrong viewport. Cutoff matches Tailwind `md` (≥768px). */}
			{isNarrow ? (
				<div className={phone ? 'flex flex-1 min-w-0' : 'flex'}>
					<Dropdown placement="bottom-end">
						<DropdownTrigger>
							<Button
								size="sm"
								variant="bordered"
								className={`border-small border-default-300 ${touchTarget} ${phone ? 'w-full' : ''}`}
								endContent={<ChevronDown className="w-4 h-4 opacity-70" aria-hidden="true" />}
							>
								<span className="font-medium">
									View:{' '}
									{visibleViews.find(v => v.key === currentViewKey)?.label?.replace(' View', '') ||
										'Week'}
								</span>
							</Button>
						</DropdownTrigger>
						<DropdownMenu
							aria-label="Switch calendar view"
							selectionMode="single"
							selectedKeys={new Set([currentViewKey])}
							onAction={key => {
								const target = visibleViews.find(v => v.key === String(key));
								if (target && typeof window !== 'undefined') {
									window.location.href = buildViewHref(target.path);
								}
							}}
						>
							{visibleViews.map(v => (
								<DropdownItem key={v.key}>{v.label}</DropdownItem>
							))}
						</DropdownMenu>
					</Dropdown>
				</div>
			) : (
				<div className="flex items-center gap-1">
					{visibleViews.map(v => {
						const isActive = v.key === currentViewKey;
						return (
							<a
								key={v.key}
								href={buildViewHref(v.path)}
								className={[
									'px-3 py-1.5 text-xs font-medium rounded-md border transition-colors duration-[var(--dur-hover)] ease-[var(--ease-out)] no-underline',
									isActive
										? 'bg-primary text-primary-foreground border-primary'
										: 'bg-default-50 text-default-700 border-default-300 hover:bg-default-100',
									FOCUS_RING
								].join(' ')}
							>
								{v.label}
							</a>
						);
					})}
				</div>
			)}

			{canCreateEvent && typeof onCreateEventClick === 'function' && (
				<Button
					color="primary"
					size="sm"
					className={`${touchTarget} ${phone ? 'flex-1 min-w-0' : ''}`}
					startContent={<Plus className="w-4 h-4" aria-hidden="true" />}
					onPress={onCreateEventClick}
				>
					Create Event
				</Button>
			)}
		</div>
	);
}

function CalendarWeekNav({
	currentDate,
	onPrevWeek,
	onNextWeek,
	onToday,
	onPickDate,
	orgUnique,
	currentViewKey = 'week',
	onCreateEventClick,
	canCreateEvent = false
}) {
	const selectedDate = useMemo(() => {
		if (!currentDate) return null;
		// Parse `YYYY-MM-DD` as a LOCAL date (not `new Date(str)`, which is UTC and
		// shifts a day back in negative-offset zones like HST — that desynced the
		// week label from the grid by a full week). Matches how the grid + week
		// bounds parse the date.
		const d =
			typeof currentDate === 'string' ? parseIsoDate(currentDate.slice(0, 10)) : currentDate;
		return d && !Number.isNaN(d.getTime()) ? d : null;
	}, [currentDate]);

	// Phone: enlarge tap targets to 44px so the Today / prev / next / week-picker
	// group never overflows the row. On phone the action group is NOT rendered
	// here — the page composes it into row 2 next to the Filters trigger.
	const { phone } = useBreakpoint();
	const touchTarget = phone ? 'min-h-[44px]' : '';
	const iconTouchTarget = phone ? 'min-w-[44px] min-h-[44px] justify-center' : '';

	const weekLabel = useMemo(
		() => (selectedDate ? formatWeekRange(selectedDate) : ''),
		[selectedDate]
	);

	// Controlled open state so re-clicking the trigger TOGGLES the week picker
	// closed. `preventOpenOnFocus` ensures only the explicit input click drives
	// opening, so the toggle is reliable.
	const [pickerOpen, setPickerOpen] = useState(false);
	// Reopen-race guard: clicking the trigger while open makes react-datepicker
	// fire `onClickOutside` FIRST (→ closes), then the trigger's `onInputClick`
	// would toggle the now-false state back to open. We timestamp the
	// outside-close and, if the input click lands within the same gesture
	// window, leave it closed instead of reopening.
	const lastOutsideCloseRef = useRef(0);

	return (
		<div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 w-full">
			{/* Phone: the date group spans the full row; Today / ‹ / › stay
			    fixed-width on the left and the week picker (flex-1) fills the
			    remaining width to the right edge — no middle gap. */}
			<div className={`flex items-center gap-1.5 min-w-0 ${phone ? 'w-full' : ''}`}>
				<button
					type="button"
					onClick={onToday}
					className={`shrink-0 inline-flex items-center justify-center px-3 py-1.5 text-sm font-medium rounded-md border border-default-300 bg-default-50 hover:bg-default-100 transition-colors duration-[var(--dur-hover)] ease-[var(--ease-out)] ${touchTarget} ${FOCUS_RING}`}
				>
					Today
				</button>
				<button
					type="button"
					onClick={onPrevWeek}
					className={`inline-flex items-center justify-center p-1.5 rounded-md border border-default-300 bg-default-50 hover:bg-default-100 shrink-0 transition-colors duration-[var(--dur-hover)] ease-[var(--ease-out)] ${iconTouchTarget} ${FOCUS_RING}`}
					aria-label="Previous week"
				>
					<svg
						xmlns="http://www.w3.org/2000/svg"
						viewBox="0 0 20 20"
						fill="currentColor"
						className="w-4 h-4"
					>
						<path
							fillRule="evenodd"
							d="M12.79 5.23a.75.75 0 01-.02 1.06L8.832 10l3.938 3.71a.75.75 0 11-1.04 1.08l-4.5-4.25a.75.75 0 010-1.08l4.5-4.25a.75.75 0 011.06.02z"
							clipRule="evenodd"
						/>
					</svg>
				</button>
				<button
					type="button"
					onClick={onNextWeek}
					className={`inline-flex items-center justify-center p-1.5 rounded-md border border-default-300 bg-default-50 hover:bg-default-100 shrink-0 transition-colors duration-[var(--dur-hover)] ease-[var(--ease-out)] ${iconTouchTarget} ${FOCUS_RING}`}
					aria-label="Next week"
				>
					<svg
						xmlns="http://www.w3.org/2000/svg"
						viewBox="0 0 20 20"
						fill="currentColor"
						className="w-4 h-4"
					>
						<path
							fillRule="evenodd"
							d="M7.21 14.77a.75.75 0 01.02-1.06L11.168 10 7.23 6.29a.75.75 0 111.04-1.08l4.5 4.25a.75.75 0 010 1.08l-4.5 4.25a.75.75 0 01-1.06-.02z"
							clipRule="evenodd"
						/>
					</svg>
				</button>
				<div className={`min-w-0 ${phone ? 'flex-1' : 'ml-2'}`}>
					<DatePicker
						selected={selectedDate}
						onChange={date => {
							onPickDate?.(date);
							setPickerOpen(false);
						}}
						open={pickerOpen}
						preventOpenOnFocus
						onInputClick={() => {
							// If an outside-close just fired for THIS same click gesture,
							// don't reopen — the trigger click that closed it must net to
							// closed.
							if (Date.now() - lastOutsideCloseRef.current < 250) return;
							setPickerOpen(o => !o);
						}}
						onClickOutside={() => {
							lastOutsideCloseRef.current = Date.now();
							setPickerOpen(false);
						}}
						// Keeps state in sync when react-datepicker closes itself (Esc,
						// blur) so the next trigger click re-opens correctly.
						onCalendarClose={() => setPickerOpen(false)}
						// Sunday-start week rows to match the legacy week view.
						calendarStartDay={0}
						// This picker chooses a WEEK, not a day — the page immediately
						// normalizes any picked date to its Sunday (weekRangeForDay). Let
						// the popup say so visually: week-picker mode highlights the whole
						// row (`react-datepicker__week--selected`, styled in
						// calendar-week-v2.css) instead of a single day, which read as if
						// one day were somehow special within the shown week (USER).
						showWeekPicker
						popperPlacement="bottom-start"
						portalId="calendar-week-datepicker-portal"
						popperProps={{ strategy: 'fixed' }}
						calendarClassName="week-view-datepicker-calendar"
						popperClassName="week-view-datepicker-popper"
						// react-datepicker's `.react-datepicker-wrapper` defaults to
						// inline-block (shrink-to-content), so the `w-full` trigger couldn't
						// stretch. Make the wrapper full-width/block on phone so the week
						// picker fills the flex-1 date row.
						wrapperClassName={phone ? 'w-full block' : undefined}
						customInput={<NavDateButton phone={phone} label={weekLabel} />}
					/>
				</div>
			</div>

			{/* Action group renders inline only on non-phone. On phone the page
			    moves it into row 2 (next to the Filters trigger). */}
			{!phone && (
				<NavActions
					currentViewKey={currentViewKey}
					orgUnique={orgUnique}
					onCreateEventClick={onCreateEventClick}
					canCreateEvent={canCreateEvent}
				/>
			)}
		</div>
	);
}

export default CalendarWeekNav;
export { NavActions };
